import { supabase } from '../lib/supabase';
import type { Transaction } from '../lib/supabase';

export type PaymentMethodType = 'pi_network' | 'stripe' | 'apple_pay' | 'google_pay' | 'credit_card';
export type ProductType = 'premium_subscription' | 'super_like' | 'boost' | 'gift' | 'verification';

export interface CreateTransactionInput {
  userId: string;
  productType: ProductType;
  amount: number;
  currency?: string;
  paymentMethod?: PaymentMethodType;
  description?: string;
  piPaymentId?: string;
  piTransactionId?: string;
  stripePaymentId?: string;
  stripeChargeId?: string;
  productDetails?: any;
  metadata?: any;
}

export class TransactionService {
  async createTransaction(input: CreateTransactionInput): Promise<Transaction | null> {
    try {
      console.log('Creating transaction:', input);

      const { data, error } = await supabase
        .from('transactions')
        .insert([{
          user_id: input.userId,
          product_type: input.productType,
          amount: input.amount,
          currency: input.currency || 'PI',
          payment_method: input.paymentMethod || 'pi_network',
          description: input.description || `${input.productType} purchase`,
          pi_payment_id: input.piPaymentId || '',
          pi_transaction_id: input.piTransactionId || '',
          stripe_payment_id: input.stripePaymentId || '',
          stripe_charge_id: input.stripeChargeId || '',
          product_details: input.productDetails || {},
          metadata: input.metadata || {},
          status: 'pending'
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating transaction:', error);
        throw error;
      }

      console.log('Transaction created:', data);
      return data;
    } catch (error) {
      console.error('Error in createTransaction:', error);
      return null;
    }
  }

  async getUserTransactions(userId: string, limit: number = 50): Promise<Transaction[]> {
    try {
      console.log('Getting transactions for user:', userId);

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting transactions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserTransactions:', error);
      return [];
    }
  }

  async getTransactionById(transactionId: string): Promise<Transaction | null> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', transactionId)
        .maybeSingle();

      if (error) {
        console.error('Error getting transaction:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getTransactionById:', error);
      return null;
    }
  }

  async updateTransactionStatus(
    transactionId: string,
    status: 'completed' | 'failed' | 'cancelled' | 'refunded',
    reason?: string
  ): Promise<Transaction | null> {
    try {
      console.log('Updating transaction status:', { transactionId, status, reason });

      const updates: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (status === 'completed') {
        updates.completed_at = new Date().toISOString();
      } else if (status === 'failed') {
        updates.failed_at = new Date().toISOString();
        if (reason) updates.failed_reason = reason;
      } else if (status === 'cancelled') {
        updates.cancelled_at = new Date().toISOString();
      } else if (status === 'refunded') {
        updates.refunded_at = new Date().toISOString();
        if (reason) updates.refund_reason = reason;
      }

      const { data, error } = await supabase
        .from('transactions')
        .update(updates)
        .eq('id', transactionId)
        .select()
        .single();

      if (error) {
        console.error('Error updating transaction:', error);
        throw error;
      }

      console.log('Transaction updated:', data);
      return data;
    } catch (error) {
      console.error('Error in updateTransactionStatus:', error);
      return null;
    }
  }

  async getTransactionsByStatus(
    userId: string,
    status: string
  ): Promise<Transaction[]> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error getting transactions by status:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getTransactionsByStatus:', error);
      return [];
    }
  }

  async getTransactionStats(userId: string): Promise<{
    totalSpent: number;
    totalTransactions: number;
    completedTransactions: number;
    failedTransactions: number;
    refundedTransactions: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('amount, status')
        .eq('user_id', userId);

      if (error) {
        console.error('Error getting transaction stats:', error);
        return {
          totalSpent: 0,
          totalTransactions: 0,
          completedTransactions: 0,
          failedTransactions: 0,
          refundedTransactions: 0
        };
      }

      const stats = (data || []).reduce(
        (acc, txn) => {
          acc.totalTransactions++;
          if (txn.status === 'completed') {
            acc.completedTransactions++;
            acc.totalSpent += Number(txn.amount);
          } else if (txn.status === 'failed') {
            acc.failedTransactions++;
          } else if (txn.status === 'refunded') {
            acc.refundedTransactions++;
          }
          return acc;
        },
        {
          totalSpent: 0,
          totalTransactions: 0,
          completedTransactions: 0,
          failedTransactions: 0,
          refundedTransactions: 0
        }
      );

      return stats;
    } catch (error) {
      console.error('Error in getTransactionStats:', error);
      return {
        totalSpent: 0,
        totalTransactions: 0,
        completedTransactions: 0,
        failedTransactions: 0,
        refundedTransactions: 0
      };
    }
  }
}

export const transactionService = new TransactionService();